import { ArrowDown, ArrowUp, Plus, ShieldCheck, X } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { V3Button } from "@/components/ui/v3/button";
import type { ReferralTemplateInput } from "@/lib/referral-templates/queries";
import {
  type ReferralSpecialtyKey,
  specialtyOptions,
  specialtyValue,
} from "@/lib/referral-templates/specialties";
import { cn } from "@/lib/utils";
import { SpecialtyField } from "./SpecialtyField";
import { ReferralSheet } from "./sheet";
import { FIELD_HINT, FIELD_INPUT, FIELD_LABEL, FIELD_TEXTAREA } from "./styles";

export type ReferralEditorValue = ReferralTemplateInput & {
  isDefault: boolean;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  initial: ReferralEditorValue;
  // Shown above the form, e.g. what was taken from an example letter.
  notice?: React.ReactNode;
  saving?: boolean;
  error?: string | null;
  onSave: (value: ReferralEditorValue) => void;
}

const MAX_SECTIONS = 20;

// Name, specialty, sections (rename, reorder, remove, add), style rules,
// layout and "Use by default". Shared by New, Edit and From an example.
export const ReferralTemplateEditor = ({
  open,
  onOpenChange,
  title,
  initial,
  notice,
  saving = false,
  error,
  onSave,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      specialtyOptions((key: ReferralSpecialtyKey) =>
        t(`referral.specialties.${key}`),
      ),
    [t],
  );
  const [value, setValue] = useState(initial);
  const [specialty, setSpecialty] = useState("");
  const [touched, setTouched] = useState(false);
  const ids = {
    name: useId(),
    specialty: useId(),
    instructions: useId(),
    layout: useId(),
    isDefault: useId(),
  };
  useEffect(() => {
    if (!open) return;
    setValue(initial);
    setSpecialty(
      initial.specialty
        ? (options.find((option) => option.name === initial.specialty)?.label ??
            initial.specialty)
        : "",
    );
    setTouched(false);
  }, [open, initial, options]);

  const sections = value.sections;
  const setSections = (next: string[]): void => {
    setValue((current) => ({ ...current, sections: next }));
  };
  const nameMissing = !value.name.trim();
  const contentMissing = !value.layout.trim() && !value.instructions.trim();
  const save = (): void => {
    setTouched(true);
    if (nameMissing || contentMissing) return;
    onSave({
      ...value,
      name: value.name.trim(),
      specialty: specialty.trim() ? specialtyValue(options, specialty) : null,
      sections: sections.map((section) => section.trim()).filter(Boolean),
    });
  };

  return (
    <ReferralSheet
      description={t("referral.editor.description")}
      open={open}
      testId="referral-template-editor"
      title={title}
      width="wide"
      onOpenChange={onOpenChange}
      onSubmit={save}
      footer={
        <>
          <V3Button
            className="max-sm:h-12"
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("common.cancel")}
          </V3Button>
          <V3Button
            className="max-sm:h-12"
            data-testid="referral-editor-save"
            disabled={saving}
            type="submit"
            variant="primary"
          >
            {saving ? t("referral.editor.saving") : t("referral.editor.save")}
          </V3Button>
        </>
      }
    >
      {notice}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label className={FIELD_LABEL} htmlFor={ids.name}>
            {t("referral.editor.name")}
          </label>
          <input
            aria-invalid={(touched && nameMissing) || undefined}
            className={cn(
              FIELD_INPUT,
              touched && nameMissing && "ring-2 ring-v3-rec",
            )}
            data-testid="referral-editor-name"
            id={ids.name}
            maxLength={100}
            type="text"
            value={value.name}
            onChange={(event) => {
              setValue((current) => ({
                ...current,
                name: event.target.value,
              }));
            }}
          />
        </div>
        <div className="space-y-1.5">
          <label className={FIELD_LABEL} htmlFor={ids.specialty}>
            {t("referral.editor.specialty")}{" "}
            <span className="font-normal text-v3-text-3">
              {t("referral.dialog.optional")}
            </span>
          </label>
          <SpecialtyField
            id={ids.specialty}
            placeholder={t("referral.editor.specialtyPlaceholder")}
            value={specialty}
            onChange={setSpecialty}
          />
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className={FIELD_LABEL}>{t("referral.editor.sections")}</legend>
        <p className={FIELD_HINT}>{t("referral.editor.sectionsHint")}</p>
        <ol className="space-y-1.5">
          {sections.map((section, index) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: rows are edited in place
              key={index}
              className="flex items-center gap-1.5"
            >
              <span className="w-5 shrink-0 text-right text-[12.5px] tabular-nums text-v3-text-3">
                {index + 1}
              </span>
              <input
                aria-label={t("referral.editor.sectionLabel", {
                  number: index + 1,
                })}
                className={cn(FIELD_INPUT, "h-10")}
                maxLength={100}
                type="text"
                value={section}
                onChange={(event) => {
                  setSections(
                    sections.map((item, i) =>
                      i === index ? event.target.value : item,
                    ),
                  );
                }}
              />
              <RowButton
                disabled={index === 0}
                label={t("referral.editor.moveUp", { name: section })}
                onClick={() => {
                  const next = [...sections];
                  [next[index - 1], next[index]] = [
                    next[index] as string,
                    next[index - 1] as string,
                  ];
                  setSections(next);
                }}
              >
                <ArrowUp aria-hidden className="size-4" />
              </RowButton>
              <RowButton
                disabled={index === sections.length - 1}
                label={t("referral.editor.moveDown", { name: section })}
                onClick={() => {
                  const next = [...sections];
                  [next[index + 1], next[index]] = [
                    next[index] as string,
                    next[index + 1] as string,
                  ];
                  setSections(next);
                }}
              >
                <ArrowDown aria-hidden className="size-4" />
              </RowButton>
              <RowButton
                label={t("referral.editor.removeSection", {
                  name: section,
                })}
                onClick={() => {
                  setSections(sections.filter((_, i) => i !== index));
                }}
              >
                <X aria-hidden className="size-4" />
              </RowButton>
            </li>
          ))}
        </ol>
        <V3Button
          disabled={sections.length >= MAX_SECTIONS}
          variant="secondary"
          onClick={() => {
            setSections([...sections, ""]);
          }}
        >
          <Plus aria-hidden />
          {t("referral.editor.addSection")}
        </V3Button>
      </fieldset>

      <div className="space-y-1.5">
        <label className={FIELD_LABEL} htmlFor={ids.instructions}>
          {t("referral.editor.instructions")}
        </label>
        <textarea
          className={cn(FIELD_TEXTAREA, "min-h-[96px]")}
          data-testid="referral-editor-instructions"
          id={ids.instructions}
          maxLength={4000}
          placeholder={t("referral.editor.instructionsPlaceholder")}
          rows={4}
          value={value.instructions}
          onChange={(event) => {
            setValue((current) => ({
              ...current,
              instructions: event.target.value,
            }));
          }}
        />
      </div>
      <div className="space-y-1.5">
        <label className={FIELD_LABEL} htmlFor={ids.layout}>
          {t("referral.editor.layout")}
        </label>
        <p className={FIELD_HINT}>{t("referral.editor.layoutHint")}</p>
        <textarea
          className={cn(
            FIELD_TEXTAREA,
            "min-h-[160px] font-mono text-[13px] leading-5 sm:text-[13px]",
          )}
          data-testid="referral-editor-layout"
          id={ids.layout}
          maxLength={6000}
          rows={8}
          value={value.layout}
          onChange={(event) => {
            setValue((current) => ({
              ...current,
              layout: event.target.value,
            }));
          }}
        />
        {touched && contentMissing && (
          <p className={cn(FIELD_HINT, "text-v3-rec-text")} role="alert">
            {t("referral.editor.contentRequired")}
          </p>
        )}
      </div>
      <label
        className="flex min-h-11 cursor-pointer items-center justify-between gap-4 rounded-xl px-3 py-2 shadow-v3-hairline"
        htmlFor={ids.isDefault}
      >
        <span className="min-w-0">
          <span className="block text-[14px] font-medium text-v3-text">
            {t("referral.editor.isDefault")}
          </span>
          <span className={FIELD_HINT}>
            {t("referral.editor.isDefaultHint")}
          </span>
        </span>
        <Switch
          checked={value.isDefault}
          className="data-[state=checked]:bg-v3-ink data-[state=unchecked]:bg-v3-surface-3 [&>span]:bg-v3-panel"
          id={ids.isDefault}
          onCheckedChange={(checked) => {
            setValue((current) => ({ ...current, isDefault: checked }));
          }}
        />
      </label>
      {error && (
        <p
          className="rounded-xl bg-v3-rec-tint px-3 py-2 text-[13.5px] text-v3-rec-text"
          role="alert"
        >
          {error}
        </p>
      )}
    </ReferralSheet>
  );
};

const RowButton = ({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element => (
  <button
    aria-label={label}
    className="flex size-10 shrink-0 items-center justify-center rounded-full text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:opacity-35 disabled:hover:bg-transparent"
    disabled={disabled}
    type="button"
    onClick={onClick}
  >
    {children}
  </button>
);

// What the example letter gave, and that no patient data was kept.
export const ExtractionNotice = ({
  identifiersRemoved,
  summary,
}: {
  identifiersRemoved: boolean;
  summary: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      className="flex gap-3 rounded-xl bg-v3-surface-2 px-3 py-3 shadow-v3-hairline text-[13.5px] leading-5 text-v3-text"
      data-testid="referral-extraction-notice"
      role="status"
    >
      <ShieldCheck
        aria-hidden
        className="mt-0.5 size-4 shrink-0 text-v3-ok-text"
      />
      <div className="space-y-1">
        <p className="font-semibold">{t("referral.example.readyTitle")}</p>
        {summary && <p className="text-v3-text-2">{summary}</p>}
        <p className="text-v3-text-2">
          {identifiersRemoved
            ? t("referral.example.identifiersRemoved")
            : t("referral.example.noIdentifiers")}
        </p>
      </div>
    </div>
  );
};
